import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { API_TYPES, post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { z } from "zod";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";

const copyWorkplaceProfileBodySchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.workplace),
    attributes: z.object({
      name: z.string(),
      coordinates: z.object({ latitude: z.number(), longitude: z.number() }).optional(),
      address: z
        .object({
          line1: z.string().optional(),
          subpremise: z.string().optional(),
          streetNumber: z.string().optional(),
          streetName: z.string().optional(),
          region: z.string().optional(),
          city: z.string().optional(),
          metropolitanStatisticalArea: z.string().optional(),
          manualMsa: z.boolean().optional(),
          isApproximateMsa: z.boolean().optional(),
          combinedStatisticalArea: z.string().optional(),
          state: z.string().optional(),
          country: z.string().optional(),
          stateCode: z.string().optional(),
          countryCode: z.string().optional(),
          postalCode: z.string().optional(),
          formatted: z.string().optional(),
        })
        .optional(),
      email: z.string().optional(),
      phone: z.string().optional(),
    }),
    relationships: z
      .object({
        sourceWorkplace: z
          .object({
            data: z.object({
              type: z.literal(API_TYPES.workplace),
              id: z.string(),
            }),
          })
          .optional(),
      })
      .optional(),
  }),
  meta: z.object({
    copy: z.object({
      notes: z.boolean(),
      pricingParameters: z.boolean(),
    }),
    isChow: z.boolean(),
  }),
});
export type CreateWorkplaceProfileBody = z.infer<typeof copyWorkplaceProfileBodySchema>;

// The copy endpoint returns the newly created workplace as a JSON:API resource. We
// only need its id and name to populate selectors (e.g. the change-of-ownership flow),
// so parse leniently and tolerate any extra attributes the backend adds.
const copyWorkplaceResponseSchema = z
  .object({
    data: z
      .object({
        id: z.string(),
        attributes: z.object({ name: z.string() }).passthrough(),
      })
      .passthrough(),
  })
  .passthrough();

export interface CreatedWorkplace {
  userId: string;
  name: string;
}

type WorkplaceAttributes = CreateWorkplaceProfileBody["data"]["attributes"];

interface CopyWorkplaceParams {
  name: string;
  sourceWorkplaceId: string;
  address?: WorkplaceAttributes["address"];
  coordinates?: WorkplaceAttributes["coordinates"];
  email?: string;
  phone?: string;
  meta: CreateWorkplaceProfileBody["meta"];
}

export function useCopyWorkplace(
  options: UseMutationOptions<CreatedWorkplace, string, CopyWorkplaceParams> = {}
) {
  return useMutation({
    mutationFn: async (params) => {
      const { name, sourceWorkplaceId, address, coordinates, email, phone, meta } = params;

      const response = await post({
        url: `${environmentConfig.REACT_APP_API_MAIN_URL}/api/workplaces/${sourceWorkplaceId}/copy`,
        data: {
          data: {
            type: "workplace",
            attributes: {
              name,
              ...(address && coordinates ? { address, coordinates } : {}),
              ...(email ? { email } : {}),
              ...(phone ? { phone } : {}),
            },
          },
          meta,
        },
        responseSchema: copyWorkplaceResponseSchema,
        requestSchema: copyWorkplaceProfileBodySchema,
      });
      return { userId: response.data.data.id, name: response.data.data.attributes.name };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.WORKPLACE_COPY_FAILURE,
    },
    ...options,
  });
}
